import { useTranslation } from 'react-i18next'
import type { ReturnsProductDetailsResponseVariantInfo } from '@itsrever/rever-api'
import { calculateAmountDue } from '../helpers/calculate-amount-due'
import {
  keepsPromotion,
  promotionalAmountDue
} from '../helpers/promotional-exchange'
import { useCredit } from '@/hooks'
import { useAppSelector } from '@/redux/hooks'

interface ProductPriceProps {
  variant: ReturnsProductDetailsResponseVariantInfo
  productId?: string
  collectionIds?: string[]
}

export const ProductPrice = ({
  variant,
  productId,
  collectionIds
}: ProductPriceProps) => {
  const {
    t,
    i18n: { language }
  } = useTranslation()

  const ecommerceGroup = useAppSelector(
    (store) => store.settingsApi.settings.response.ecommerce_group
  )

  const { remainingCredit, promotionalExchange, availablePromotionalUnits } =
    useCredit()

  const parsedVariantPrice = variant.price?.amount
    ? parseFloat(variant.price?.amount)
    : null

  const keepsItsPromotion =
    availablePromotionalUnits > 0 &&
    keepsPromotion(promotionalExchange, { productId, collectionIds })

  const amountDue =
    keepsItsPromotion && promotionalExchange
      ? promotionalAmountDue(
          promotionalExchange,
          parsedVariantPrice,
          remainingCredit
        )
      : parsedVariantPrice !== null
        ? calculateAmountDue(parsedVariantPrice, remainingCredit)
        : null

  // A replacement keeping the promotion is priced from it, whatever credit is left
  const showsAmountDue = keepsItsPromotion || remainingCredit > 0

  const isHoff = ecommerceGroup === 'hoff'
  const isMisako = ecommerceGroup === 'misako'

  if (isHoff) {
    return showsAmountDue ? (
      <div className="flex shrink-0 items-center gap-2">
        <p className="text-xl font-medium text-black">
          {amountDue !== null
            ? amountDue?.toLocaleString(language, {
                style: 'currency',
                currency: variant.price?.currency_code
              })
            : null}
        </p>
        <p className="text-xs font-light text-neutral-500">
          {parsedVariantPrice !== null
            ? parsedVariantPrice?.toLocaleString(language, {
                style: 'currency',
                currency: variant.price?.currency_code
              })
            : null}{' '}
          {t('full_catalog.original_price')}
        </p>
      </div>
    ) : parsedVariantPrice !== null ? (
      <p className="text-xl font-medium text-black">
        {parsedVariantPrice?.toLocaleString(language, {
          style: 'currency',
          currency: variant.price?.currency_code
        })}
      </p>
    ) : null
  }

  if (isMisako) {
    return showsAmountDue ? (
      <div className="flex shrink-0 items-center gap-2">
        <p className="text-xl">
          {amountDue !== null
            ? amountDue?.toLocaleString(language, {
                style: 'currency',
                currency: variant.price?.currency_code
              })
            : null}
        </p>
        <p className="text-right text-xs font-light uppercase tracking-wider text-neutral-400">
          {parsedVariantPrice !== null
            ? parsedVariantPrice?.toLocaleString(language, {
                style: 'currency',
                currency: variant.price?.currency_code
              })
            : null}{' '}
          {t('full_catalog.original_price')}
        </p>
      </div>
    ) : parsedVariantPrice !== null ? (
      <p className="text-xl">
        {parsedVariantPrice?.toLocaleString(language, {
          style: 'currency',
          currency: variant.price?.currency_code
        })}
      </p>
    ) : null
  }

  return showsAmountDue ? (
    <div className="flex shrink-0 items-center gap-2">
      <p
        style={{
          boxShadow: '0px 0px 5.7px 0px #FFF inset'
        }}
        className="flex w-fit shrink-0 items-center justify-center rounded-full bg-radial-gradient from-brand-800 to-brand-800 px-3 py-0.5 text-base text-white md:text-lg"
      >
        {amountDue !== null
          ? amountDue?.toLocaleString(language, {
              style: 'currency',
              currency: variant.price?.currency_code
            })
          : null}
      </p>
      <p className="text-xs font-light text-neutral-400 md:text-sm">
        {parsedVariantPrice !== null
          ? parsedVariantPrice?.toLocaleString(language, {
              style: 'currency',
              currency: variant.price?.currency_code
            })
          : null}{' '}
        {t('full_catalog.original_price')}
      </p>
    </div>
  ) : parsedVariantPrice !== null ? (
    <p className="text-base md:text-xl">
      {parsedVariantPrice?.toLocaleString(language, {
        style: 'currency',
        currency: variant.price?.currency_code
      })}
    </p>
  ) : null
}
